import type { DisplayMode } from './types';

/**
 * Sequential ramp for travel time.
 *
 * One hue (OKLCH ~65°, amber), monotone lightness, seven steps — validated with
 * the dataviz ordinal checks against the dark app surface (#12100e):
 * monotone L, every adjacent gap >= 0.06, dark end 2.19:1, hue spread 1°.
 *
 * The ramp is deliberately *bright at the fast end*. What the map encodes is how
 * well-connected a location is, so the places worth noticing glow and the poorly
 * connected ones recede into the dark basemap. Blue — the documented default
 * sequential hue — is unusable here because water on the basemap is blue.
 *
 * The lightness range spans 0.92→0.42 rather than hugging the light end: two
 * near-white top steps were indistinguishable once drawn over a dark map.
 */
export const RAMP = ['#fedec0', '#ffb973', '#f69508', '#d27f06', '#b06903', '#8f5401', '#6c420f'];

/**
 * Width of one isochrone band, in minutes. Ten rather than fifteen: journeys
 * people actually weigh up cluster in the 20–50 minute range, and 15-minute
 * bands collapsed most of the city into two colours.
 */
export const BAND_MINUTES = 10;

/**
 * The finer alternative. Fourteen steps over the same range, which is past the
 * point where adjacent swatches are comfortably distinguishable on their own —
 * it works because the contour lines land on every band boundary and do the
 * separating. Use it to read gradients within a neighbourhood; the 10-minute
 * bands are the better default for taking in the city at a glance.
 */
export const FINE_BAND_MINUTES = 5;

/** Ramp saturates here; the final band is "this and above". */
export const MAX_MINUTES = BAND_MINUTES * RAMP.length; // 70

/** Ink used for contour lines and hairlines drawn over the ramp. */
export const CONTOUR_INK = '#171310';

/**
 * Berlin S-Bahn green for the navigation overlay, brightened from the official
 * badge green so it stays legible over the light end of the amber ramp. This is
 * an identity mark, not part of the data encoding.
 */
export const SBAHN_GREEN = '#00b862';
export const SBAHN_CASING = '#07100b';

/** Band width for a display mode; 'smooth' still needs one for the contours. */
export function bandMinutesFor(display: DisplayMode): number {
  return display === 'bands5' ? FINE_BAND_MINUTES : BAND_MINUTES;
}

export function bandCount(bandMinutes: number): number {
  return Math.max(1, Math.round(MAX_MINUTES / bandMinutes));
}

export function bandLabels(bandMinutes: number = BAND_MINUTES): string[] {
  const n = bandCount(bandMinutes);
  return Array.from({ length: n }, (_, i) =>
    i === n - 1 ? `${i * bandMinutes}+` : `${i * bandMinutes}–${(i + 1) * bandMinutes}`
  );
}

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16) / 255,
    parseInt(h.slice(2, 4), 16) / 255,
    parseInt(h.slice(4, 6), 16) / 255,
  ];
}

/** Flat rgb triples for a GLSL `uniform vec3[]`. */
export function rampAsFloats(): Float32Array {
  const out = new Float32Array(RAMP.length * 3);
  RAMP.forEach((hex, i) => {
    const [r, g, b] = hexToRgb(hex);
    out[i * 3] = r;
    out[i * 3 + 1] = g;
    out[i * 3 + 2] = b;
  });
  return out;
}

function rgbToHex([r, g, b]: [number, number, number]): string {
  const h = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

/**
 * Sample the ramp at t ∈ [0,1]. Mirrors `rampSmooth()` in the heatmap shader —
 * same linear interpolation, same stops — so the legend can't drift from what
 * the map actually draws.
 */
export function sampleRamp(t: number): string {
  const x = Math.min(1, Math.max(0, t)) * (RAMP.length - 1);
  const i0 = Math.floor(x);
  const i1 = Math.min(i0 + 1, RAMP.length - 1);
  const f = x - i0;
  const a = hexToRgb(RAMP[i0]);
  const b = hexToRgb(RAMP[i1]);
  return rgbToHex([a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f]);
}

/**
 * The colour of each band, quantising the ramp into however many bands the
 * chosen width gives. At 10 minutes this returns RAMP itself, unchanged.
 */
export function bandColors(bandMinutes: number): string[] {
  const n = bandCount(bandMinutes);
  return Array.from({ length: n }, (_, i) => sampleRamp(n === 1 ? 0 : i / (n - 1)));
}

/** CSS colour for a given number of minutes — used by legend and readouts. */
export function colorForMinutes(minutes: number): string {
  const i = Math.min(RAMP.length - 1, Math.max(0, Math.floor(minutes / BAND_MINUTES)));
  return RAMP[i];
}

export function formatMinutes(minutes: number): string {
  if (!Number.isFinite(minutes)) return '—';
  const total = Math.round(minutes);
  if (total < 60) return `${total} min`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** Mode bitmask from the pipeline, for stop badges. */
export const MODE_BITS: { bit: number; short: string; name: string }[] = [
  { bit: 1, short: 'S', name: 'S-Bahn' },
  { bit: 2, short: 'U', name: 'U-Bahn' },
  { bit: 4, short: 'T', name: 'Tram' },
  { bit: 8, short: 'B', name: 'Bus' },
  { bit: 16, short: 'R', name: 'Regional' },
  { bit: 32, short: 'F', name: 'Ferry' },
];

export function modeShorts(modes: number): { short: string; name: string }[] {
  return MODE_BITS.filter((m) => modes & m.bit).map(({ short, name }) => ({ short, name }));
}

/**
 * Dot colours for the stop overlay. Same identity idea as the badges, but
 * brightened like SBAHN_GREEN: a badge sits on a dark panel, a dot sits on the
 * light end of the amber ramp and has to survive both backgrounds.
 */
export const MODE_DOT = {
  S: SBAHN_GREEN,
  U: '#3b8fe8',
  T: '#f0503c',
  B: '#cf63bc',
  R: '#c9c2b8',
  F: '#25c0e0',
} as const;

/**
 * Which mode a stop is drawn as. Most stops serve several — nearly every rail
 * station also has a bus stop — so one wins: rail first, bus last, because bus
 * is the default and says the least about a location.
 */
const DOT_PRIORITY = [
  { bit: 1, short: 'S' },
  { bit: 2, short: 'U' },
  { bit: 16, short: 'R' },
  { bit: 4, short: 'T' },
  { bit: 32, short: 'F' },
] as const;

export function primaryMode(modes: number): string {
  return DOT_PRIORITY.find((m) => modes & m.bit)?.short ?? 'B';
}
